import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, TextField,
  InputAdornment, CircularProgress, Alert, Avatar
} from '@mui/material';
import { Add, Edit, Delete, Search, Star, StarBorder } from '@mui/icons-material';
import api from '../services/api';

export default function Experts() {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchExperts();
  }, []);

  const fetchExperts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/experts');
      setExperts(res.data);
    } catch (err) {
      setError('Erreur lors du chargement des experts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet expert ?')) return;
    try {
      await api.delete(`/experts/${id}`);
      setExperts(experts.filter(e => e._id !== id));
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const toggleFeatured = async (expert) => {
    try {
      await api.put(`/experts/${expert._id}`, { isFeatured: !expert.isFeatured });
      setExperts(experts.map(e =>
        e._id === expert._id ? { ...e, isFeatured: !e.isFeatured } : e
      ));
    } catch (err) {
      setError('Erreur lors de la mise a jour');
    }
  };

  const filteredExperts = experts.filter(e =>
    `${e.firstName} ${e.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
    e.specialty?.toLowerCase().includes(search.toLowerCase()) ||
    e.institution?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Experts</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/experts/new')}
        >
          Nouvel Expert
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Rechercher un expert..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Specialite</TableCell>
              <TableCell>Institution</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredExperts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Aucun expert trouve
                </TableCell>
              </TableRow>
            ) : (
              filteredExperts.map((expert) => (
                <TableRow key={expert._id} hover>
                  <TableCell>
                    <Avatar src={expert.image} alt={expert.lastName}>
                      {expert.firstName?.charAt(0)}{expert.lastName?.charAt(0)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {expert.title} {expert.firstName} {expert.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {expert.specialty}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {expert.institution}
                    </Typography>
                    {expert.city && (
                      <Typography variant="caption" color="text.secondary">
                        {expert.city}, {expert.country}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {expert.isActive ? (
                      <Chip label="Actif" size="small" color="success" />
                    ) : (
                      <Chip label="Inactif" size="small" color="default" />
                    )}
                    <IconButton
                      size="small"
                      onClick={() => toggleFeatured(expert)}
                      color={expert.isFeatured ? 'warning' : 'default'}
                    >
                      {expert.isFeatured ? <Star /> : <StarBorder />}
                    </IconButton>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/experts/${expert._id}`)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(expert._id)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          {filteredExperts.length} expert(s) - {filteredExperts.filter(e => e.isFeatured).length} mis en avant
        </Typography>
      </Box>
    </Box>
  );
}
